<template>
  <div class="app-detail-page">
    <PageHeader
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="app-detail-header">
          <div class="app-detail-logo" aria-hidden="true">
            <span>{{ saleCode }}</span>
          </div>

          <div class="app-detail-heading">
            <h2>{{ saleName }}</h2>
            <div class="app-detail-heading-meta">
              <span class="app-detail-code">{{ saleHeadingSecondaryLabel }}</span>
              <span
                class="app-status-pill app-detail-status-pill"
                :class="saleIsActive ? 'app-status-active' : 'app-status-inactive'"
              >
                {{ saleStatusLabel }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="`app-header-btn--${action.variant || 'primary'}`"
            type="button"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span>{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.moreActions"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </PageHeader>

    <div class="app-detail-content">
      <section v-if="loading" class="app-detail-loading card">
        <div class="card-body">{{ text.detail.loading }}</div>
      </section>

      <template v-else>
        <section class="metrics-grid app-detail-kpis sale-detail-kpis-five">
          <SalesKpiCard
            v-for="metric in detailMetrics"
            :key="metric.key"
            :title="metric.title"
            :value="metric.value"
            :value-meta="metric.valueMeta"
            :value-meta-items="metric.valueMetaItems"
            :value-meta-lines="metric.valueMetaLines"
            :extra-values="metric.extraValues"
            :value-scale="metric.valueScale"
            :icon="metric.icon"
            :icon-bg="metric.iconBg"
            :icon-color="metric.iconColor"
          />
        </section>

        <AppSectionTabs
          v-model="activeTab"
          :tabs="tabs"
          :aria-label="text.detail.sectionTabsAriaLabel"
        />

        <SaleDetailTabsPanel
          :active-tab="activeTab"
          :payment-plan-initial-customer-id="paymentPlanInitialCustomerId"
          :payment-plan-open-modal-request-id="paymentPlanOpenModalRequestId"
          :sale="sale"
          :expense-rows="paginatedExpenseRows"
          :expense-loading="expenseLoading"
          :expense-filter-options="expenseFilterOptions"
          :expense-pagination="expensePagination"
          :expense-status-tabs="expenseStatusTabs"
          :expense-active-status-tab="expenseActiveStatusTab"
          :expense-summary="expenseSummary"
          :fee-rows="paginatedFeeRows"
          :fee-loading="feeLoading"
          :fee-filter-options="feeFilterOptions"
          :fee-pagination="feePagination"
          :fee-status-tabs="feeStatusTabs"
          :fee-active-status-tab="feeActiveStatusTab"
          :fee-summary="feeSummary"
          @edit-section="handleEditDetailSection"
          @expenses-search-change="handleSaleExpensesSearchChange"
          @expenses-filters-change="handleSaleExpensesFiltersChange"
          @expenses-status-tab-select="handleSaleExpensesStatusTabSelect"
          @expenses-page-change="handleSaleExpensesPageChange"
          @clear-expenses-from-sale="handleSaleClearExpensesFilters"
          @new-expense-from-sale="handleSaleNewExpenseFromExpenses"
          @pay-expense="handleSalePayExpense"
          @edit-expense="handleSaleEditExpense"
          @delete-expense="handleSaleDeleteExpense"
          @approve-expense="handleSaleApproveExpense"
          @download-xml="handleSaleDownloadXml"
          @download-pdf="handleSaleDownloadPdf"
          @download-receipt="handleSaleDownloadReceipt"
          @fees-search-change="handleSaleFeesSearchChange"
          @fees-filters-change="handleSaleFeesFiltersChange"
          @fees-status-tab-select="handleSaleFeesStatusTabSelect"
          @fees-page-change="handleSaleFeesPageChange"
          @pay-fee="handleSalePayFee"
          @edit-fee="handleSaleEditFee"
          @delete-fee="handleSaleDeleteFee"
          @approve-fee="handleSaleApproveFee"
          @download-fee-xml="handleSaleDownloadFeeXml"
          @download-fee-pdf="handleSaleDownloadFeePdf"
          @download-fee-receipt="handleSaleDownloadFeeReceipt"
          @clear-fees-from-sale="handleSaleClearFeesFilters"
          @new-fee-from-sale="handleSaleNewFeeFromFees"
          @new-payment-plan="handleSaleNewPaymentPlan"
          @payment-plan-saved="handleSalePaymentPlanSaved"
          @invoice-plan="handleSaleInvoicePlan"
          @open-payment-plan-for-customer="handleOpenPaymentPlanForCustomer"
          @open-history-tab="handleOpenHistoryTab"
        />
      </template>
    </div>

    <SaleModal
      :show="isSaleEditModalOpen"
      :customer-mode="isMultiCustomerSale ? 'multi' : 'single'"
      :service-filter-mode="isFlatRateSale ? 'flat-rate' : 'auto'"
      mode="edit"
      :initial-data="sale"
      :initial-section-key="saleEditSectionKey"
      :submitting="saleEditSubmitting"
      :advertising-enabled="isAdvertisingModuleActive"
      @close="closeSaleEditModal"
      @delete="handleDeleteSaleFromModal"
      @submit="handleSaleEditSubmit"
    />

    <ConfirmActionModal
      :show="isDeleteSaleConfirmModalOpen"
      :title="deleteSaleConfirmTitle"
      :subtitle="deleteSaleConfirmSubtitle"
      :message="deleteSaleConfirmMessage"
      :confirm-text="text.detail.deleteModal.confirm"
      @cancel="closeDeleteSaleConfirmModal"
      @confirm="confirmDeleteSaleFromDetail"
    />

    <ExpenseModal
      :show="isExpenseModalOpen"
      :mode="expenseModalMode"
      :initial-data="expenseModalInitialData"
      :locked-sale-id="saleId"
      :submitting="isExpenseSubmitting"
      @close="closeExpenseModal"
      @submit="submitSaleExpense"
    />

    <ConfirmActionModal
      :show="isApproveExpenseModalOpen"
      :title="expenseApproveModal.title"
      :subtitle="expenseApproveModal.subtitle"
      :message="expenseApproveModal.message"
      :confirm-text="expenseApproveModal.confirmText"
      :cancel-text="expenseApproveModal.cancelText"
      @cancel="closeSaleApproveExpenseModal"
      @confirm="confirmSaleApproveExpense"
    />

    <ConfirmActionModal
      :show="isDeleteExpenseModalOpen"
      :title="expenseDeleteModal.title"
      :subtitle="expenseDeleteModal.subtitle"
      :message="expenseDeleteModal.message"
      :confirm-text="expenseDeleteModal.confirmText"
      :cancel-text="expenseDeleteModal.cancelText"
      @cancel="closeSaleDeleteExpenseModal"
      @confirm="confirmSaleDeleteExpense"
    />

    <PayModal
      :show="isPayExpenseModalOpen"
      :row="payExpenseTarget"
      context="expense-fee"
      entity-type="expense"
      :submitting="isPayExpenseSubmitting"
      @close="closeSalePayExpenseModal"
      @submit="submitSalePayExpense"
    />

    <FeeModal
      :show="isFeeModalOpen"
      :mode="feeModalMode"
      :initial-data="feeModalInitialData"
      :locked-sale-id="saleId"
      :submitting="isFeeSubmitting"
      @close="closeFeeModal"
      @submit="submitSaleFee"
    />

    <ConfirmActionModal
      :show="isApproveFeeModalOpen"
      :title="feeApproveModal.title"
      :subtitle="feeApproveModal.subtitle"
      :message="feeApproveModal.message"
      :confirm-text="feeApproveModal.confirmText"
      :cancel-text="feeApproveModal.cancelText"
      @cancel="closeSaleApproveFeeModal"
      @confirm="confirmSaleApproveFee"
    />

    <ConfirmActionModal
      :show="isDeleteFeeModalOpen"
      :title="feeDeleteModal.title"
      :subtitle="feeDeleteModal.subtitle"
      :message="feeDeleteModal.message"
      :confirm-text="feeDeleteModal.confirmText"
      :cancel-text="feeDeleteModal.cancelText"
      @cancel="closeSaleDeleteFeeModal"
      @confirm="confirmSaleDeleteFee"
    />

    <PayModal
      :show="isPayFeeModalOpen"
      :row="payFeeTarget"
      context="expense-fee"
      entity-type="fee"
      :submitting="isPayFeeSubmitting"
      @close="closeSalePayFeeModal"
      @submit="submitSalePayFee"
    />

    <ConfirmActionModal
      :show="isClearSaleExpensesConfirmModalOpen"
      :title="text.detail.clearExpenses.title"
      :subtitle="clearSaleExpensesConfirmSubtitle"
      :message="clearSaleExpensesConfirmMessage"
      :confirm-text="text.detail.clearExpenses.confirm"
      @cancel="closeClearSaleExpensesConfirmModal"
      @confirm="confirmClearSaleExpenses"
    />

    <ConfirmActionModal
      :show="isClearSaleFeesConfirmModalOpen"
      :title="text.detail.clearFees.title"
      :subtitle="clearSaleFeesConfirmSubtitle"
      :message="clearSaleFeesConfirmMessage"
      :confirm-text="text.detail.clearFees.confirm"
      @cancel="closeClearSaleFeesConfirmModal"
      @confirm="confirmClearSaleFees"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import SalesKpiCard from '@/components/sales/SalesKpiCard.vue';
import SaleDetailTabsPanel from '@/components/sales/shared/detail/SaleDetailTabsPanel.vue';
import ExpenseModal from '@/components/expenses/ExpenseModal.vue';
import FeeModal from '@/components/fees/FeeModal.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import {
  createSaleDetailState,
  saleDetailComputed,
  saleDetailMethods,
} from '@/composables/sales/useSaleDetailPage';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  resolveExpenseApproveModalContent,
  resolveExpenseDeleteModalContent,
} from '@/composables/expenses/useExpenseFlow';
import {
  resolveFeeApproveModalContent,
  resolveFeeDeleteModalContent,
} from '@/composables/fees/useFeeFlow';

const { catalog: text } = useDomainText('sales');

export default {
  name: 'SaleDetailPage',
  components: {
    PageHeader,
    AppSectionTabs,
    SalesKpiCard,
    SaleDetailTabsPanel,
    ExpenseModal,
    FeeModal,
    PayModal,
    SaleModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      ...createSaleDetailState(),
      showMoreActionsMenu: false,
    };
  },
  computed: {
    ...saleDetailComputed,
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
    saleCustomerName() {
      return String(
        this.sale?.company_name
        || this.sale?.customer_name
        || this.sale?.company?.name
        || this.sale?.customer?.name
        || ''
      ).trim();
    },
    saleHeadingSecondaryLabel() {
      if (this.isMultiCustomerSale) {
        return this.text.detail.labels.multiCustomer;
      }

      return this.saleCustomerName;
    },
    deleteSaleConfirmTitle() {
      if (this.isMultiCustomerSale) {
        return this.text.detail.deleteModal.multiCustomer.title;
      }

      if (this.isFlatRateSale) {
        return this.text.detail.deleteModal.flatRate.title;
      }

      return this.text.detail.deleteModal.business.title;
    },
    deleteSaleConfirmSubtitle() {
      if (this.isMultiCustomerSale) {
        return this.text.detail.deleteModal.multiCustomer.subtitle;
      }

      if (this.isFlatRateSale) {
        return this.text.detail.deleteModal.flatRate.subtitle;
      }

      return this.text.detail.deleteModal.business.subtitle;
    },
    deleteSaleConfirmMessage() {
      const saleName = String(
        this.deletingSaleFromDetail?.sale_name
        || this.deletingSaleFromDetail?.name
        || this.sale?.sale_name
        || this.sale?.name
        || ''
      ).trim() || this.text.detail.labels.noName;

      if (this.isMultiCustomerSale) {
        return `${this.text.detail.deleteModal.multiCustomer.messagePrefix}: ${saleName}\n${this.text.detail.deleteModal.question}`;
      }

      if (this.isFlatRateSale) {
        return `${this.text.detail.deleteModal.flatRate.messagePrefix}: ${saleName}\n${this.text.detail.deleteModal.question}`;
      }

      return `${this.text.detail.deleteModal.business.messagePrefix}: ${saleName}\n${this.text.detail.deleteModal.question}`;
    },
    primaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const preferredOrder = ['edit-sale', 'sale-sheet'];

      return preferredOrder
        .map((key) => actions.find((action) => String(action?.key || '') === key))
        .filter(Boolean);
    },
    secondaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));

      return actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
    },
    expenseApproveModal() {
      return resolveExpenseApproveModalContent({
        row: this.approveExpenseTarget,
        approveTargetState: this.approveExpenseTargetState,
        isApproving: this.isApprovingExpense,
      });
    },
    expenseDeleteModal() {
      return resolveExpenseDeleteModalContent({
        row: this.deleteExpenseTarget,
        isDeleting: this.isDeletingExpense,
      });
    },
    feeApproveModal() {
      return resolveFeeApproveModalContent({
        row: this.approveFeeTarget,
        approveTargetState: this.approveFeeTargetState,
        isApproving: this.isApprovingFee,
      });
    },
    feeDeleteModal() {
      return resolveFeeDeleteModalContent({
        row: this.deleteFeeTarget,
        isDeleting: this.isDeletingFee,
      });
    },
  },
  mounted() {
    this.fetchSaleDetail();
    document.addEventListener('click', this.handleHeaderDocumentClick);
  },
  beforeUnmount() {
    this.cleanupSaleDetail();
    document.removeEventListener('click', this.handleHeaderDocumentClick);
  },
  watch: {
    saleId(nextId, previousId) {
      if (nextId !== previousId) {
        this.fetchSaleDetail();
      }
    },
    '$route.query.tab'() {
      this.applyRequestedTabKey();
    },
    activeTab(nextTab, previousTab) {
      if (nextTab !== previousTab && nextTab === 'expenses') {
        this.fetchSaleExpenses();
        return;
      }

      if (nextTab !== previousTab && nextTab === 'commissions') {
        this.fetchSaleFees();
      }

      if (nextTab !== 'payment-plan') {
        this.paymentPlanInitialCustomerId = 0;
      }
    },
  },
  methods: {
    ...saleDetailMethods,
    handlePrimaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction(action);
    },
    toggleMoreActionsMenu() {
      this.showMoreActionsMenu = !this.showMoreActionsMenu;
    },
    handleSecondaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction(action);
    },
    handleHeaderDocumentClick(event) {
      const target = event?.target;
      if (target && target.closest('.app-detail-more-actions-wrap')) {
        return;
      }

      this.showMoreActionsMenu = false;
    },
    handleOpenPaymentPlanForCustomer(customerId) {
      const normalizedCustomerId = Number(customerId || 0) || 0;
      if (normalizedCustomerId <= 0) {
        return;
      }

      this.openSalePaymentPlanModal(normalizedCustomerId);
    },
    handleOpenHistoryTab() {
      const tabList = Array.isArray(this.tabs) ? this.tabs : [];
      const hasHistoryTab = tabList.some((tab) => String(tab?.key || '') === 'history');

      if (hasHistoryTab) {
        this.activeTab = 'history';
      }
    },
    handleSaleEditSubmit(payload) {
      if (this.isMultiCustomerSale) {
        this.submitSaleMultiCustomerEdit(payload);
        return;
      }

      this.submitSaleEdit(payload);
    },
  },
};
</script>

<style scoped>
.sale-detail-kpis-five {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.app-detail-logo {
  width: auto;
  min-width: 88px;
  max-width: 190px;
  height: 64px;
  padding: 0 12px;
  border-radius: 14px;
  text-align: center;
}

.app-detail-logo span {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.app-detail-heading-meta .app-detail-code {
  font-size: 14px;
}

.app-detail-customer-line {
  margin: 4px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.app-detail-customer-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.app-detail-customer-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

@media (max-width: 1500px) {
  .sale-detail-kpis-five {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .sale-detail-kpis-five {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .app-detail-logo {
    max-width: 150px;
  }
}

@media (max-width: 767px) {
  .sale-detail-kpis-five {
    grid-template-columns: 1fr;
  }
}
</style>
